import React from 'react';
import Page from 'components/new/Page';
import { LP_CAPITAL_ACTION_LIST } from 'adminConstants/routes';
import { useHistory, useParams } from 'react-router-dom';
import { useCapitalActionNotice } from './useCapitalActionNotice';
import { Controller, useForm } from 'react-hook-form';
import { CreationStep, useCreationStep } from './useCreationStep';
import { zodResolver } from '@hookform/resolvers/zod';
import { CreateNoticeSchemaFormValues, schema } from './schema';
import Row from 'components/Layout/Row';
import Button from '@mui/material/Button';
import { useDisclosure } from 'further-ui/hooks';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import InfoOutline from '@mui/icons-material/InfoOutline';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNotification } from 'hooks/ui/useNotification';
import Stepper from 'components/new/Stepper';
import { styled } from '@mui/material/styles';
import DataEntryForm from './DataEntryForm';
import Complete from './Complete';
import { useLayoutContext } from 'contexts/LayoutContext';
import {
  mapCapitalActionNoticeCreatorDataToFormValues,
  mapFormValuesToSaveMutationInput,
  mapFullyValidatedFormValuesToSaveMutationInput,
} from './formMapping';
import { match } from 'ts-pattern';
import Switch from 'further-ui/components/Switch';
import { CapitalActionNoticeStatus } from 'further-types/lp/capital-action-notice';

const StyledStepper = styled(Stepper)(() => ({
  '& .MuiStepConnector-line': {
    minWidth: 80,
    flexShrink: 0,
  },
}));

const CreateCapitalActionNotice: React.FC = () => {
  const capitalActionIdFromParams = useParams<{ id: string }>().id;
  const { vehicle } = useLayoutContext();
  const { data, save, publish, saving } = useCapitalActionNotice({
    capitalActionId: capitalActionIdFromParams,
    vehicleId: vehicle?._id ?? '',
  });
  const publishDialog = useDisclosure();
  const cancelDialog = useDisclosure();
  const saveAndExitWhenPublishedDialog = useDisclosure();
  const history = useHistory();
  const { error } = useNotification();

  const {
    setValue,
    formState: { isDirty },
    ...form
  } = useForm<CreateNoticeSchemaFormValues>({
    resolver: zodResolver(schema),
    values: mapCapitalActionNoticeCreatorDataToFormValues(data),
  });

  const dataIsValid = schema.safeParse({
    ...mapCapitalActionNoticeCreatorDataToFormValues(data),
    useFullValidation: true,
  }).success;

  const { step, setStep } = useCreationStep(dataIsValid);

  const isPublished = data.status === CapitalActionNoticeStatus.Published;
  const perInvestorBreakdown = form.watch('perInvestorBreakdown');
  const investorsWillBeNotified = perInvestorBreakdown?.some(
    (investor) => investor.notifyViaEmail,
  );
  const resendPublishedNotices = form.watch('resendPublishedNotices');
  const emailsWillBeSent = match({
    isPublished,
    investorsWillBeNotified,
    resendPublishedNotices,
  })
    .with(
      {
        isPublished: true,
      },
      ({ investorsWillBeNotified, resendPublishedNotices }) =>
        resendPublishedNotices && investorsWillBeNotified,
    )
    .otherwise(() => investorsWillBeNotified);

  const handleProceed = () => {
    setValue('useFullValidation', true);
    form.handleSubmit(
      (formData) => {
        if (!formData.useFullValidation) return;
        const mappedData = mapFullyValidatedFormValuesToSaveMutationInput({
          ...formData,
          vehicleId: data.vehicleId,
          capitalActionId: data.capitalActionId,
          id: data.id,
          status: data.status,
        });
        if (step === CreationStep.CreateNotice) {
          save(mappedData, { onSuccess: () => setStep(CreationStep.Summary) });
        } else if (publishDialog.isOpen) {
          publish(mappedData, {
            onSuccess: () => setStep(CreationStep.Completion),
          });
        } else {
          publishDialog.onOpen();
        }
      },
      () => {
        error('Please check the page for errors before proceeding.');
      },
    )();
  };

  const handleRetreat = () => {
    if (step === CreationStep.Summary) {
      setStep(CreationStep.CreateNotice);
    } else if (
      step === CreationStep.CreateNotice &&
      !cancelDialog.isOpen &&
      isDirty
    ) {
      cancelDialog.onOpen();
    } else {
      history.push(LP_CAPITAL_ACTION_LIST);
    }
  };

  const handleSaveAndExit = () => {
    if (
      isDirty &&
      isPublished &&
      !saveAndExitWhenPublishedDialog.isOpen &&
      !cancelDialog.isOpen
    ) {
      saveAndExitWhenPublishedDialog.onOpen();
      return;
    }
    saveAndExitWhenPublishedDialog.onClose();
    if (data.status === CapitalActionNoticeStatus.Published) {
      setValue('useFullValidation', true);
    }
    form.handleSubmit(
      (formData) => {
        const mappedData = mapFormValuesToSaveMutationInput({
          ...formData,
          vehicleId: data.vehicleId,
          capitalActionId: data.capitalActionId,
          id: data.id,
          status: data.status,
        });
        save(mappedData, {
          onSuccess: () => history.push(LP_CAPITAL_ACTION_LIST),
        });
      },
      () => {
        error(
          'Unable to save your changes, please check the page for errors before proceeding.',
        );
      },
    )();
  };

  return (
    <Page
      tabTitle="Capital actions: capital action notice"
      goBackAction={LP_CAPITAL_ACTION_LIST}
      heading="Capital action notice"
      withHeadingDivider
      withStickyHeader
      customActions={
        <StyledStepper
          steps={['Create capital action notice', 'Summary']}
          activeStep={match(step)
            .with(CreationStep.Completion, () => 2)
            .with(CreationStep.Summary, () => 1)
            .otherwise(() => 0)}
        />
      }
      spacing="lg"
      actionBar={
        <Row
          justify={
            step === CreationStep.Completion ? 'flex-end' : 'space-between'
          }
        >
          {step === CreationStep.Completion ? (
            <Button onClick={handleRetreat} variant="outlined" size="large">
              Exit
            </Button>
          ) : (
            <>
              <Button size="large" variant="outlined" onClick={handleRetreat}>
                {step === CreationStep.CreateNotice ? 'Cancel' : 'Previous'}
              </Button>
              <Row spacing="sm" centered>
                {step === CreationStep.Summary && isPublished ? (
                  <Row centered>
                    <Tooltip title="Toggle this on if you want to resend email notifications to investors when you re-publish this capital notice.">
                      <span>
                        <Row spacing="xxs" centered>
                          <Typography variant="h6">Resend notices</Typography>
                          <InfoOutline fontSize="inherit" />
                        </Row>
                      </span>
                    </Tooltip>
                    <Controller
                      control={form.control}
                      name="resendPublishedNotices"
                      render={({ field }) => (
                        <Switch
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Row>
                ) : null}
                <Button
                  onClick={handleSaveAndExit}
                  variant="outlined"
                  size="large"
                  loading={saving}
                >
                  Save and exit
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleProceed}
                >
                  {step === CreationStep.CreateNotice
                    ? 'Review summary'
                    : 'Publish notices'}
                </Button>
              </Row>
            </>
          )}
        </Row>
      }
    >
      <ConfirmDialog
        maxWidth="sm"
        title="Exit without publishing"
        content={
          <Typography variant="body2">
            You are about to exit without publishing your changes. Your changes
            will be saved for editing later.
          </Typography>
        }
        actions={{
          confirm: {
            label: 'Exit page',
            onClick: handleSaveAndExit,
          },
        }}
        onClose={saveAndExitWhenPublishedDialog.onClose}
        open={saveAndExitWhenPublishedDialog.isOpen}
      />

      <ConfirmDialog
        maxWidth="sm"
        content={
          <Typography variant="body2">
            You are about to publish this capital call notice and investors will{' '}
            {!emailsWillBeSent ? ' not ' : ' '}be notified by email. Notice
            information can be edited after sending. Do you wish to proceed?
          </Typography>
        }
        title="Publishing capital notice"
        actions={{
          confirm: { onClick: handleProceed, label: 'Publish notices' },
        }}
        onClose={publishDialog.onClose}
        open={publishDialog.isOpen}
      />
      <ConfirmDialog
        maxWidth="sm"
        content={
          <Typography variant="body2">
            You are about to leave without saving. All progress will be lost. Do
            you want to save before exiting?
          </Typography>
        }
        title="Exit notice creation"
        actions={{
          alternateConfirm: {
            onClick: handleRetreat,
            label: 'Exit without saving',
          },
          confirm: {
            onClick: handleSaveAndExit,
            label: 'Save and exit',
          },
        }}
        onClose={cancelDialog.onClose}
        open={cancelDialog.isOpen}
      />
      {step === CreationStep.Completion ? (
        <Complete />
      ) : (
        <DataEntryForm
          setValue={setValue}
          capitalActionData={data}
          formControl={form.control}
          isReadOnly={step !== CreationStep.CreateNotice}
        />
      )}
    </Page>
  );
};

export default CreateCapitalActionNotice;
